import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/card/BackLink";
import { useScreenLabel } from "@/lib/screenTrail";
import { DossierEditButton } from "@/features/dossiers/DossierEditButton";
import { DossierTabs } from "@/features/dossiers/DossierTabs";
import { STATUS_LABEL_KEYS, STATUS_CLASS } from "@/features/dossiers/DossierDrawer";
import { Skeleton } from "@/components/feedback/Skeleton";
import {
  getDossier,
  listActiveParties,
  listDossierDocuments,
  listDossierExternalOrders,
  listDossierWlxOrders,
  listDossierAuditEvents,
  dossiersKeys,
} from "@/features/dossiers/dossiers.api";
import { hasModule } from "@/lib/auth";

// No redirect without the right: a dossier is opened from a link elsewhere (an order, a warehouse order), and a silent
// jump to the dashboard left the user guessing (support, 20-10-2026). The screen says why instead.
export const Route = createFileRoute("/_shell/dossiers/$id")({
  component: DossierDetailRoute,
});

function DossierDetailRoute() {
  const { session } = Route.useRouteContext();
  return hasModule(session, "dossiers") ? <DossierDetailScreen /> : <NoDossierAccess />;
}

function NoDossierAccess() {
  const { t } = useTranslation();
  return (
    <div className="wl-page" data-screen-label="Back-office Dossier Detail">
      <h1 className="t-h2">{t("dossiers.noAccess")}</h1>
      <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
        {t("dossiers.noAccessDesc")}
      </p>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function DossierDetailScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { session } = Route.useRouteContext();

  const dossierQuery = useQuery({
    queryKey: dossiersKeys.detail(id),
    queryFn: () => getDossier(id),
    retry: false,
  });
  const partiesQuery = useQuery({
    queryKey: dossiersKeys.activeParties,
    queryFn: listActiveParties,
  });
  // Secondary data — isolated queries, so a hiccup never fails the page.
  const documentsQuery = useQuery({
    queryKey: dossiersKeys.documents(id),
    queryFn: () => listDossierDocuments(id),
  });
  const ordersQuery = useQuery({
    queryKey: dossiersKeys.externalOrders(id),
    queryFn: () => listDossierExternalOrders(id),
  });
  const wlxOrdersQuery = useQuery({
    queryKey: dossiersKeys.wlxOrders(id),
    queryFn: () => listDossierWlxOrders(id),
  });
  const auditQuery = useQuery({
    queryKey: dossiersKeys.auditEvents(id),
    queryFn: () => listDossierAuditEvents(id),
  });
  useScreenLabel(dossierQuery.data?.internalDossierNumber);

  if (dossierQuery.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office Dossier Detail" aria-busy="true">
        {/* Header skeleton — mirrors the loaded layout's geometry */}
        <div style={{ marginBottom: "28px" }}>
          <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "10px" }}>
            <Skeleton width="180px" height="var(--space-7)" />
            <Skeleton width="96px" height="var(--space-6)" />
          </div>
          <Skeleton width="40%" height="var(--space-3)" />
        </div>
        <Skeleton width="240px" height="var(--space-6)" />
        <div style={{ marginTop: "20px" }}>
          <Skeleton height="320px" />
        </div>
      </div>
    );
  }

  if (dossierQuery.isError || !dossierQuery.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Dossier Detail">
        <h1 className="t-h2">{t("dossiers.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("dossiers.notFoundDesc")}
        </p>
      </div>
    );
  }

  const dossier = dossierQuery.data;
  const createdDate = formatDate(dossier.createdAt);
  const updatedDate = formatDate(dossier.updatedAt);

  return (
    <div className="wl-page" data-screen-label="Back-office Dossier Detail">
      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        {/* Back to the list as you left it — the breadcrumb's job before the
            navigation moved to the top. See lib/listState. */}
        <BackLink list="/dossiers" listLabel={t("nav.dossiers")} style={{ marginBottom: "var(--space-2)" }} />
        <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "6px" }}>
          <h1 className="t-h2" style={{ margin: 0 }}>
            {dossier.internalDossierNumber ?? "—"}
          </h1>
          <span className={`wl-status ${STATUS_CLASS[dossier.status] ?? "wl-status--planned"}`}>
            <span className="wl-status__dot" />
            {STATUS_LABEL_KEYS[dossier.status]
              ? t(STATUS_LABEL_KEYS[dossier.status] as never)
              : dossier.status}
          </span>
          <div className="wl-spacer" />
          <DossierEditButton dossier={dossier} parties={partiesQuery.data ?? []} />
        </div>
        <div className="wl-hstack t-body-sm t-muted" style={{ gap: "16px" }}>
          <span>{dossier.party.name}</span>
          <span>·</span>
          <span>{t("dossiers.createdLabel", { date: createdDate })}</span>
          <span>·</span>
          <span>{t("dossiers.updatedLabel", { date: updatedDate })}</span>
          {dossier.folderStatus === "created" && dossier.sharePointFolderWebUrl && (
            <>
              <span>·</span>
              <a
                href={dossier.sharePointFolderWebUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--text-brand)", fontWeight: "var(--fw-medium)" }}
              >
                {t("dossiers.sharePointFolder")}
              </a>
            </>
          )}
          {dossier.folderStatus === "pending" && (
            <>
              <span>·</span>
              <span>{t("dossiers.sharePointPending")}</span>
            </>
          )}
          {dossier.folderStatus === "failed" && (
            <>
              <span>·</span>
              <span className="wl-status wl-status--exception">
                <span className="wl-status__dot" />
                {t("dossiers.sharePointFailed")}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Detail card */}
      <div className="wl-card" style={{ marginBottom: "24px", padding: "20px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          <div>
            <div className="t-caption" style={{ marginBottom: "4px" }}>{t("dossiers.primaryReference")}</div>
            <div className="t-body-sm" style={{ fontWeight: "var(--fw-medium)" }}>
              {dossier.primaryReference || <span className="t-muted">—</span>}
            </div>
          </div>
          <div>
            <div className="t-caption" style={{ marginBottom: "4px" }}>{t("dossiers.party")}</div>
            <div className="t-body-sm">{dossier.party.name}</div>
            <div className="t-mono t-muted" style={{ fontSize: "11px" }}>{dossier.party.code}</div>
          </div>
          {dossier.description && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div className="t-caption" style={{ marginBottom: "4px" }}>{t("dossiers.description")}</div>
              <div className="t-body-sm">{dossier.description}</div>
            </div>
          )}
        </div>
      </div>

      {/* Documents · Orders · Activity */}
      <DossierTabs
        documents={documentsQuery.data ?? []}
        orders={ordersQuery.data ?? []}
        wlxOrders={wlxOrdersQuery.data ?? []}
        dossierId={id}
        canWrite={hasModule(session, "dossiers_write")}
        onDocumentsChanged={() => void documentsQuery.refetch()}
        auditEvents={auditQuery.data?.events ?? []}
        auditTruncated={auditQuery.data?.truncated ?? false}
      />
    </div>
  );
}
